import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Col,
  Row,
  Card,
  Radio,
  message,
  Empty,
  Space,
  Divider,
  Tag
} from 'antd';
import FormItem from '@/common/FormItem';
import CustomButton from '@/common/CustomButton';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styles from '@/common/Layout/index.module.less';
import { qacreateChecklist } from './../redux/slice';
import { getQcMaterialDetails } from '../../../redux/slice';
import {
  getCampaignBatches,
  getSamplingCampaignBatches
} from '@/features/lineclearancechecklist/redux/slice';
import { printLC } from '../redux/slice';
import CustomTable from 'common/Content/CustomTable';
import { columns } from './constants.js';
import { history } from 'app/history';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import { getLoggedInUser } from 'utilities/helpers';

const CreateLineClearanceChecklistPage = () => {
  const [form] = Form.useForm();
  const [wHLC, setWHLC] = useState({});
  const [qALC, setQALC] = useState({});
  const [isPrintDisabled, setIsPrintDisabled] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [dispenseId, setDispenseId] = useState('');

  const dispatch = useDispatch();
  const params = useParams();
  const { user } = getLoggedInUser();
  const searchParam = useLocation().search;
  const searchParams = new URLSearchParams(searchParam);

  const { lcMaterialDetails } = useSelector((state) => state.lcMaterialDetails);
  const material = lcMaterialDetails?.dispensing_request?.type;

  // Helper function to check if sampling type
  const isSamplingType = () => searchParams?.get('type') === 'sampling';

  // Helper function to check if approved
  const isApproved = () => {
    if (isSamplingType()) {
      return lcMaterialDetails?.lc_status === 'QALC Approved';
    }
    return lcMaterialDetails?.dispensing_request?.status === 'Approved';
  };

  // Helper function to get response text
  const getResponseText = (value) => {
    if (value == 1) return 'Yes';
    if (value == 2) return 'No';
    if (value == 3) return 'NA';
    return 'Not Filled';
  };

  // Helper function to render approval status
  const renderApprovalStatus = (whValue, qaValue) => (
    <Space size={16}>
      {wHLC &&
        Object.keys(wHLC).length > 0 &&
        `WH: ${getResponseText(whValue)}`}
      {qALC &&
        Object.keys(qALC).length > 0 &&
        `QA: ${getResponseText(qaValue)}`}
    </Space>
  );

  // Helper function for rendering form items with radio groups
  const renderRadioFormItem = (label, name, whField, qaField) => (
    <Row gutter={24}>
      <Col xs={24} xl={24} span={24}>
        <FormItem
          label={label}
          name={name}
          rules={
            !isApproved()
              ? [
                  {
                    required: true,
                    message: `Please select ${label.toLowerCase()}`
                  }
                ]
              : []
          }
        >
          {isApproved() ? (
            renderApprovalStatus(wHLC?.[whField], qALC?.[qaField])
          ) : (
            <Radio.Group>
              <Radio value={1}>Yes</Radio>
              <Radio value={2}>No</Radio>
              <Radio value={3}>NA</Radio>
            </Radio.Group>
          )}
        </FormItem>
      </Col>
    </Row>
  );

  // Sampling campaigns can hold more than one batch (they share an lc_clearence_uuid),
  // so render every batch in the campaign - the same set the LC PDF prints. Falls back
  // to the batch on screen until the campaign call settles.
  const renderSamplingBatchTags = () =>
    campaigns?.length ? (
      campaigns.map((batch) => (
        <Tag key={batch?.id} style={{ fontSize: '14px' }}>
          {batch?.inhouse_batch_number}
        </Tag>
      ))
    ) : (
      <Tag style={{ fontSize: '14px' }}>
        {lcMaterialDetails?.inhouse_batch_number}
      </Tag>
    );

  useEffect(() => {
    handleGetAllMaterials();
    if (!isSamplingType()) {
      handleGetAllCampaignBatches();
    }
  }, [dispenseId]);

  /* Sampling resolves its campaign from the line clearance on the fetched batch, so it
     survives a refresh / direct link instead of depending on localStorage. */
  useEffect(() => {
    if (isSamplingType() && lcMaterialDetails) {
      handleGetSamplingCampaignBatches();
    }
  }, [lcMaterialDetails]);

  const handleGetAllMaterials = () => {
    let payload = {
      id: params.id,
      type: searchParams?.get('type')
    };
    dispatch(getQcMaterialDetails(payload)).then(function (result) {
      if (result?.payload?.success) {
        if (user?.role === 'user') {
          setDispenseId(
            result?.payload?.data?.dispensing_request?.id || params.id
          );
        } else {
          setDispenseId(params.id);
        }
      }
    });
  };

  const handleGetAllCampaignBatches = () => {
    let payload = {
      dispensing_request_id: localStorage.getItem('batches')
        ? String(localStorage.getItem('batches')).split(',')
        : dispenseId
    };
    dispatch(getCampaignBatches(payload)).then(function (result) {
      if (result?.payload?.success) {
        setCampaigns(result?.payload?.data);
      }
    });
  };

  const handleGetSamplingCampaignBatches = () => {
    const stored = localStorage.getItem('batches');
    let campaign_ids = [];

    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        campaign_ids = Array.isArray(parsed) ? parsed : [parsed];
      } catch (e) {
        campaign_ids = String(stored).split(',').filter(Boolean);
      }
    }

    /* Re-entry after a refresh / direct link has no campaign list — send the id kind the
       API expects for this role and let it rebuild the campaign from lc_clearence_uuid. */
    if (!campaign_ids.length) {
      const fallback_id =
        user?.role === 'user'
          ? lcMaterialDetails?.id
          : lcMaterialDetails?.line_clearance_batches?.[0]?.id;
      if (!fallback_id) return;
      campaign_ids = [fallback_id];
    }

    dispatch(
      getSamplingCampaignBatches({ batch_details_id: campaign_ids })
    ).then(function (result) {
      if (result?.payload?.success) {
        setCampaigns(result?.payload?.data);
      }
    });
  };

  useEffect(() => {
    if (!lcMaterialDetails) return;

    const lc_filter = isSamplingType()
      ? lcMaterialDetails?.line_clearance_batches?.[0]?.qa_line_clearance_batches?.filter(
          (item) => item?.lc_type === 'lc_initiated'
        )
      : lcMaterialDetails?.list_clerance_checklists?.filter(
          (item) => item?.lc_type === 'lc_initiated'
        );

    const lc_qa_filter = isSamplingType()
      ? lcMaterialDetails?.line_clearance_batches?.[0]?.qa_line_clearance_batches?.filter(
          (item) => item?.lc_type === 'lc_submitted'
        )
      : lcMaterialDetails?.list_clerance_checklists?.filter(
          (item) => item?.lc_type === 'lc_submitted'
        );

    setWHLC(lc_filter?.[0] || {});
    setQALC(lc_qa_filter?.[0] || {});

    // Get data based on type
    const getData = (field) => {
      if (isSamplingType()) {
        return lcMaterialDetails?.line_clearance_batches?.[0]?.[field];
      }
      return lcMaterialDetails?.[field];
    };

    // Format datetime
    const formatDateTime = (dateTime) => {
      return dateTime ? moment(dateTime).format('YYYY-MM-DD HH:mm') : '';
    };

    form.setFieldsValue({
      bmr_number: lcMaterialDetails?.dispensing_request?.bmr_number,
      product_name: lcMaterialDetails?.dispensing_request?.product_detail?.name,
      product_code: lcMaterialDetails?.dispensing_request?.product_batch_number,
      dispense_sampling_booth_id: getData('dispense_sampling_booth_id'),
      date: getData('date'),
      previous_product_name: getData('previous_product_name'),
      previous_product_batch_no: isSamplingType()
        ? getData('previous_product_batch_no')
        : lcMaterialDetails?.previous_product_batch_no_display ||
          getData('previous_product_batch_no'),
      proposed_product_name: isSamplingType()
        ? lcMaterialDetails?.material?.name
        : lcMaterialDetails?.proposed_product_name || 'NA',
      proposed_product_batch_no: isSamplingType()
        ? lcMaterialDetails?.inhouse_batch_number
        : lcMaterialDetails?.proposed_product_batch_no || 'NA',
      area_ahu_switched_on: getData('area_ahu_switched_on') || 'NA',
      area_ahu_time_date: formatDateTime(getData('area_ahu_time_date')),
      laf_switched_on: getData('laf_switched_on') || 'NA',
      laf_time_date: formatDateTime(getData('laf_time_date')),
      magnehelle_guage_reading_dispensing_samping_area:
        getData('magnehelle_guage_reading_dispensing_samping_area') || 'NA',
      temperature: getData('temperature') || 'NA',
      relative_humidity: getData('relative_humidity') || 'NA',
      pre_filter: getData('pre_filter') || 'NA',
      fine_filter: getData('fine_filter') || 'NA',
      hepa_filter: getData('hepa_filter') || 'NA'
    });
  }, [lcMaterialDetails]);

  const onFinishFailed = (errorInfo) => {
    console.log('Failed:', errorInfo);
    console.log('Error fields:', errorInfo.errorFields);
    if (errorInfo.errorFields.length > 0) {
      errorInfo.errorFields.forEach((field) => {
        console.log('Missing field:', field.name[0], field.errors);
      });
      message.error(
        'Please select a response to all the questions in the checklist'
      );
    }
  };

  const printLcPdf = () => {
    setIsPrintDisabled(true);
    const payload = {
      id: isSamplingType()
        ? lcMaterialDetails?.line_clearance_batches?.[0]?.id
        : params.id,
      type: searchParams?.get('type')
    };
    dispatch(printLC(payload)).then(function (response) {
      setIsPrintDisabled(false);
      if (response?.payload?.success) {
        window.open(
          `${process.env.REACT_APP_STORAGE_URL}${response?.payload?.url
            .split('/')
            .pop()}`,
          '_blank'
        );
      }
    });
  };

  const onFinish = (values) => {
    console.log(
      'Form values:',
      isSamplingType(),
      values,
      localStorage.getItem('batches')
    );

    /* Sampling approvals are keyed on line_clearance_batch ids. Take them from the
       campaign the API resolved so a refresh / direct link still submits the whole
       campaign instead of falling back to the URL id, which is a batch_details id. */
    const sampling_lc_ids = isSamplingType()
      ? (campaigns || [])
          .map((batch) => batch?.line_clearance_batches?.[0]?.id)
          .filter(Boolean)
      : [];

    // Add additional required fields
    values['compaign_batches'] = sampling_lc_ids.length
      ? sampling_lc_ids
      : localStorage.getItem('batches')
      ? user?.role === 'qc_initiator' && isSamplingType()
        ? JSON.parse(localStorage.getItem('batches'))
        : String(localStorage.getItem('batches')).split(',')
      : [params.id];
    // localStorage.getItem('batches')
    //   ? String(localStorage.getItem('batches')).split(',')
    //   : isSamplingType() && user?.role === 'qc_initiator'
    //   ? lcMaterialDetails?.line_clearance_batches?.[0]?.id
    //   : [params.id];
    values['type'] = searchParams?.get('type');

    console.log('Final form values:', values);

    dispatch(qacreateChecklist(values)).then(function (response) {
      if (response?.payload?.success) {
        message.success(response.payload.message);
        if (isSamplingType()) {
          history.push('/batches-list?type=lc_sampling');
        } else {
          history.push('/dispense-material-list');
        }
        form.resetFields();
      }
    });
  };

  let locale = {
    emptyText: (
      <span>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        <div className={styles.empty_state_text}>
          There are no material requests to display
        </div>
      </span>
    )
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
    >
      <Card
        className={styles.cardStyles_approver}
        title={`Line Clearance Checklist - ${
          material === 'bmr' ? 'BMR Based' : 'Material Code Based'
        }`}
        extra={
          <Space>
            <CustomButton
              type="primary"
              ghost
              onClick={printLcPdf}
              disabled={isPrintDisabled}
            >
              Print
            </CustomButton>
          </Space>
        }
      >
        <Row gutter={24}>
          {material === 'bmr' ? (
            <>
              <Col xs={24} xl={6} span={6}>
                <FormItem
                  label="BMR Number"
                  name="bmr_number"
                  className={styles.customLable}
                >
                  <Input disabled />
                </FormItem>
              </Col>
              <Col span={6}>
                <FormItem label="Product Name" className={styles.customLable}>
                  <Input
                    disabled
                    value={
                      lcMaterialDetails?.dispensing_request?.product_detail
                        ?.name || 'NA'
                    }
                  />
                </FormItem>
              </Col>
              <Col span={6}>
                <FormItem label="Effective Date" className={styles.customLable}>
                  <Input
                    disabled
                    value={
                      lcMaterialDetails?.dispensing_request?.product_detail
                        ?.effective_date
                        ? moment(
                            lcMaterialDetails?.dispensing_request
                              ?.product_detail?.effective_date
                          ).format('DD MMM YYYY')
                        : 'NA'
                    }
                  />
                </FormItem>
              </Col>
              <Col span={6}>
                <FormItem label="Batch Size" className={styles.customLable}>
                  <Input
                    disabled
                    value={
                      lcMaterialDetails?.dispensing_request?.product_detail
                        ?.theoretical_batch_size || 'NA'
                    }
                  />
                </FormItem>
              </Col>
              <Col span={6}>
                <FormItem label="MFC Number" className={styles.customLable}>
                  <Input
                    disabled
                    value={
                      lcMaterialDetails?.dispensing_request?.ref_mfc || 'NA'
                    }
                  />
                </FormItem>
              </Col>
              <Col span={6}>
                <FormItem label="Product Code" className={styles.customLable}>
                  <Input
                    disabled
                    value={
                      lcMaterialDetails?.dispensing_request?.product_code ||
                      'NA'
                    }
                  />
                </FormItem>
              </Col>
              <Col span={6}>
                <FormItem label="Label Claim" className={styles.customLable}>
                  <Input
                    disabled
                    value={
                      lcMaterialDetails?.dispensing_request?.label_claim || 'NA'
                    }
                  />
                </FormItem>
              </Col>
              <Col span={6}>
                <FormItem
                  label="Manufacturing Date"
                  className={styles.customLable}
                >
                  <Input
                    disabled
                    value={
                      lcMaterialDetails?.dispensing_request?.manufacturing_date
                        ? moment(
                            lcMaterialDetails?.dispensing_request
                              ?.manufacturing_date
                          ).format('YYYY-MM-DD')
                        : 'NA'
                    }
                  />
                </FormItem>
              </Col>
              <Col span={6}>
                <FormItem label="Expiry Date" className={styles.customLable}>
                  <Input
                    disabled
                    value={
                      lcMaterialDetails?.dispensing_request?.expiry_date || 'NA'
                    }
                  />
                </FormItem>
              </Col>
              <Col span={6}>
                <FormItem
                  label="Manufacturing Site"
                  className={styles.customLable}
                >
                  <Input
                    disabled
                    value={
                      lcMaterialDetails?.dispensing_request
                        ?.manufacturing_site || 'NA'
                    }
                  />
                </FormItem>
              </Col>
              <Col span={6}>
                <FormItem
                  label="Market / Customer Details"
                  className={styles.customLable}
                >
                  <Input
                    disabled
                    value={
                      lcMaterialDetails?.dispensing_request?.customer_details ||
                      'NA'
                    }
                  />
                </FormItem>
              </Col>
              <Col xs={24} xl={16} span={8}>
                <FormItem
                  label="Product Batch Number"
                  name="product_code"
                  className={styles.customLable}
                >
                  {isSamplingType()
                    ? renderSamplingBatchTags()
                    : campaigns?.map((batch) => (
                        <Tag key={batch?.id} style={{ fontSize: '14px' }}>
                          {`${batch?.product_batch_number || ''}${
                            batch?.prod_label_process_master
                              ?.process_label_name ||
                            batch?.prod_label_sub_process?.sub_process_name
                              ? ` (${
                                  batch?.prod_label_process_master
                                    ?.process_label_name || 'NA'
                                }${
                                  batch?.prod_label_sub_process
                                    ?.sub_process_name
                                    ? ` › ${batch?.prod_label_sub_process?.sub_process_name}`
                                    : ''
                                })`
                              : ''
                          }`}
                        </Tag>
                      ))}
                </FormItem>
              </Col>
            </>
          ) : null}
        </Row>

        {material === 'material' ? (
          <>
            <CustomTable
              pagination={false}
              locale={locale}
              columns={columns}
              dataSource={
                lcMaterialDetails?.dispensing_request?.dispensing_materials
              }
              className={styles.scrolling}
              scroll={{ x: 'auto' }}
            />
          </>
        ) : null}

        <Divider />

        <Row gutter={24}>
          <Col xs={24} xl={12} span={12}>
            <FormItem
              label="Dispensing Sample Booth ID No"
              name="dispense_sampling_booth_id"
              className={styles.customLable}
            >
              <Input disabled />
            </FormItem>
          </Col>
          <Col xs={24} xl={12} span={12}>
            <FormItem label="Date" name="date" className={styles.customLable}>
              <Input disabled />
            </FormItem>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24} xl={12} span={12}>
            <FormItem
              label="Previous Product Name"
              name="previous_product_name"
              className={styles.customLable}
            >
              <Input disabled />
            </FormItem>
          </Col>
          <Col xs={24} xl={12} span={12}>
            <FormItem
              label="Previous Batch No"
              name="previous_product_batch_no"
              className={styles.customLable}
            >
              <Input disabled />
            </FormItem>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24} xl={12} span={12}>
            <FormItem
              label="Proposed Product Name"
              name="proposed_product_name"
              className={styles.customLable}
            >
              <Input disabled />
            </FormItem>
          </Col>
          <Col xs={24} xl={12} span={12}>
            <FormItem
              label="Proposed Batch No"
              name="proposed_product_batch_no"
              className={styles.customLable}
            >
              {isSamplingType()
                ? renderSamplingBatchTags()
                : campaigns?.map((batch) => (
                    <Tag key={batch?.id} style={{ fontSize: '14px' }}>
                      {`${batch?.product_batch_number || ''}${
                        batch?.prod_label_process_master?.process_label_name ||
                        batch?.prod_label_sub_process?.sub_process_name
                          ? ` (${
                              batch?.prod_label_process_master
                                ?.process_label_name || 'NA'
                            }${
                              batch?.prod_label_sub_process?.sub_process_name
                                ? ` › ${batch?.prod_label_sub_process?.sub_process_name}`
                                : ''
                            })`
                          : ''
                      }`}
                    </Tag>
                  ))}
            </FormItem>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24} xl={12} span={12}>
            <FormItem
              label="Area AHU Switched Status"
              name="area_ahu_switched_on"
              className={styles.customLable}
            >
              <Input disabled />
            </FormItem>
          </Col>
          {(isSamplingType()
            ? lcMaterialDetails?.line_clearance_batches?.[0]
                ?.area_ahu_switched_on
            : lcMaterialDetails?.area_ahu_switched_on) === 'on' ? (
            <Col xs={24} xl={12} span={12}>
              <FormItem
                label="Time & Date"
                name="area_ahu_time_date"
                className={styles.customLable}
              >
                <Input disabled />
              </FormItem>
            </Col>
          ) : null}

          <Col xs={24} xl={12} span={12}>
            <FormItem
              label="LAF Switched Status"
              name="laf_switched_on"
              className={styles.customLable}
            >
              <Input disabled />
            </FormItem>
          </Col>
          {(isSamplingType()
            ? lcMaterialDetails?.line_clearance_batches?.[0]?.laf_switched_on
            : lcMaterialDetails?.laf_switched_on) === 'on' ? (
            <Col xs={24} xl={12} span={12}>
              <FormItem
                label="Time & Date"
                name="laf_time_date"
                className={styles.customLable}
              >
                <Input disabled />
              </FormItem>
            </Col>
          ) : null}
        </Row>

        <Row gutter={24}>
          <Col xs={24} xl={24} span={24}>
            <FormItem
              label="Magnehelic gauge reading Dispensing Sample Area"
              name="magnehelle_guage_reading_dispensing_samping_area"
              className={styles.customLable}
            >
              <Input disabled />
            </FormItem>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24} xl={12} span={12}>
            <FormItem
              label="Temperature(°C)"
              name="temperature"
              className={styles.customLable}
            >
              <Input disabled />
            </FormItem>
          </Col>
          <Col xs={24} xl={12} span={12}>
            <FormItem
              label="(%)Relative Humidity"
              name="relative_humidity"
              className={styles.customLable}
            >
              <Input disabled />
            </FormItem>
          </Col>
        </Row>
      </Card>

      <br />

      <Card
        className={styles.cardStyles_approver}
        title="LAF Magnehelle Gauge Reading"
      >
        <Row gutter={24}>
          <Col xs={24} xl={8} span={8}>
            <FormItem
              label="Pre Filter"
              name="pre_filter"
              className={styles.customLable}
            >
              <Input disabled />
            </FormItem>
          </Col>
          <Col xs={24} xl={8} span={8}>
            <FormItem
              label="Fine Filter"
              name="fine_filter"
              className={styles.customLable}
            >
              <Input disabled />
            </FormItem>
          </Col>
          <Col xs={24} xl={8} span={8}>
            <FormItem
              label="HEPA Filter"
              name="hepa_filter"
              className={styles.customLable}
            >
              <Input disabled />
            </FormItem>
          </Col>
        </Row>

        <Divider />

        {/* Checklist Questions */}
        {renderRadioFormItem(
          'No containers of previous material were observed in the area',
          'no_containers_of_previous_material',
          'no_containers_of_previous_material',
          'no_containers_of_previous_material'
        )}

        {renderRadioFormItem(
          'Checked for the unavailability of used dispensing/sampling aids in the area',
          'checked_unavailablity_used_dispensing_sample_aids_area',
          'sample_aids_area',
          'sample_aids_area'
        )}

        {renderRadioFormItem(
          'Checked for the unavailability of visible powder observed around the balance',
          'checked_unavailablity_vivible_powder_observed_around_the_area',
          'vivible_powder',
          'vivible_powder'
        )}

        {renderRadioFormItem(
          'Dustbins are cleared, and scraps are removed',
          'dustbins_are_cleared',
          'dustbins_are_cleared',
          'dustbins_are_cleared'
        )}

        {renderRadioFormItem(
          'Cleaning of floor, Stainless steel panels of dispensing/sampling booth',
          'cleaning_of_floor',
          'cleaning_of_floor',
          'cleaning_of_floor'
        )}

        {renderRadioFormItem(
          'Cleaning of the covings',
          'cleaning_of_covings',
          'cleaning_of_covings',
          'cleaning_of_covings'
        )}

        {renderRadioFormItem(
          'Cleaning of the electrical fixtures of dispensing/sampling booth',
          'cleaning_of_electrical',
          'cleaning_of_electrical',
          'cleaning_of_electrical'
        )}

        {renderRadioFormItem(
          'Previous records removed from the area',
          'previous_record_removed',
          'previous_record_removed',
          'previous_record_removed'
        )}

        {renderRadioFormItem(
          'Cleanliness of the working area',
          'cleaniness_working_area',
          'cleaniness_working_area',
          'cleaniness_working_area'
        )}

        {renderRadioFormItem(
          'Balance calibration carried out',
          'balance_calbration_carried_out',
          'balance_calbration_carried_out',
          'balance_calbration_carried_out'
        )}

        {renderRadioFormItem(
          'Checked the packing condition of the to be dispensed/sampling materials and found satisfactory',
          'check_the_packing_condition',
          'check_the_packing_condition',
          'check_the_packing_condition'
        )}

        {renderRadioFormItem(
          'Checked the material name, material code, approved status etc. by scanning of QR code of to be dispensed material',
          'checked_the_material',
          'checked_the_material',
          'checked_the_material'
        )}

        {renderRadioFormItem(
          'Checked the retest date, best before use date and expiry date of to be dispensed/sampling material through QR code scanning and found satisfactory',
          'checked_the_retest_date',
          'checked_the_retest_date',
          'checked_the_retest_date'
        )}

        {renderRadioFormItem(
          'Swab results of previous product (If any) are found satisfactory',
          'swab_results_of_previous_product',
          'swab_results',
          'swab_results'
        )}

        {renderRadioFormItem(
          'Availability & cleanliness of nitrogen purging',
          'availability_cleanness_nitrogen',
          'availability_cleanness_nitrogen',
          'availability_cleanness_nitrogen'
        )}

        {renderRadioFormItem(
          'Sodium vapor lamps required for proposed product',
          'sodium_vapour_lamps_required_proposed_product',
          'sodium_vapour_lamps',
          'sodium_vapour_lamps'
        )}

        {renderRadioFormItem(
          'Check the cleanliness of pass box',
          'cleaniness_of_pass_box',
          'cleaniness_of_pass_box',
          'cleaniness_of_pass_box'
        )}

        {renderRadioFormItem(
          'Ensure the cleanliness and cleanliness validity of the dispensing aids as per the status label',
          'ensure_the_cleanliness_and_validity',
          'ensure_the_cleanliness_and_validity',
          'ensure_the_cleanliness_and_validity'
        )}

        {renderRadioFormItem(
          'Ensure the cleanliness of the Label counting machine and verify the usage logbook up-dation',
          'ensure_the_cleanliness_of_the_label',
          'ensure_the_cleanliness_of_the_label',
          'ensure_the_cleanliness_of_the_label'
        )}

        {renderRadioFormItem(
          'Ensure that if any gaps observed in dispensing /sampling booth shall be closed with suitable',
          'ensure_that_if_any_gaps_observed',
          'ensure_that_if_any_gaps_observed',
          'ensure_that_if_any_gaps_observed'
        )}
      </Card>

      <br />

      {!isApproved() && (
        <div className={styles.submitbutton}>
          <FormItem className="text-center">
            <CustomButton type="primary" htmlType="submit">
              Submit
            </CustomButton>
          </FormItem>
        </div>
      )}
    </Form>
  );
};

export default CreateLineClearanceChecklistPage;
